import { slaWeights, scoreBands, scoreDescriptions, demoReviewScores, calculateWeightedScore, getBandForScore } from "@/mocks/slaMatrix";

const categoryKeys = ["quality", "timeliness", "value", "communication", "compliance"] as const;

const shortLabels: Record<string, string> = {
  quality: "Quality of Service",
  timeliness: "Timeliness & Delivery",
  value: "Value for Money",
  communication: "Communication",
  compliance: "Compliance",
};

function WeightBar({ weight, color }: { weight: number; color: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-2.5 bg-background-200 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${color}`}
          style={{ width: `${weight}%` }}
        ></div>
      </div>
      <span className="text-foreground-700 text-sm font-semibold w-10 text-right">{weight}%</span>
    </div>
  );
}

export default function SLAMatrix() {
  const demoWeighted = calculateWeightedScore(demoReviewScores);
  const demoBand = getBandForScore(demoWeighted);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-primary-600 text-xl md:text-2xl font-bold">SLA Scoring Matrix</h1>
        <p className="text-foreground-500 text-sm mt-1">
          Weighted scoring framework used to evaluate supplier performance and determine renewal recommendations
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-background-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-md bg-primary-50 flex items-center justify-center">
              <i className="ri-bar-chart-grouped-line text-primary-600 text-sm"></i>
            </div>
            <p className="text-foreground-500 text-xs font-medium">Categories</p>
          </div>
          <p className="text-foreground-800 text-2xl font-bold">{slaWeights.length}</p>
          <p className="text-foreground-400 text-xs mt-0.5">weighted scoring dimensions</p>
        </div>
        <div className="bg-white rounded-lg border border-background-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-md bg-secondary-50 flex items-center justify-center">
              <i className="ri-scales-3-line text-secondary-600 text-sm"></i>
            </div>
            <p className="text-foreground-500 text-xs font-medium">Total Weight</p>
          </div>
          <p className="text-foreground-800 text-2xl font-bold">100%</p>
          <p className="text-foreground-400 text-xs mt-0.5">balanced across categories</p>
        </div>
        <div className="bg-white rounded-lg border border-background-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-md bg-accent-50 flex items-center justify-center">
              <i className="ri-stack-line text-accent-600 text-sm"></i>
            </div>
            <p className="text-foreground-500 text-xs font-medium">Score Bands</p>
          </div>
          <p className="text-foreground-800 text-2xl font-bold">{scoreBands.length}</p>
          <p className="text-foreground-400 text-xs mt-0.5">outcome classification tiers</p>
        </div>
        <div className="bg-white rounded-lg border border-background-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-md bg-background-200 flex items-center justify-center">
              <i className="ri-file-list-3-line text-foreground-500 text-sm"></i>
            </div>
            <p className="text-foreground-500 text-xs font-medium">Score Levels</p>
          </div>
          <p className="text-foreground-800 text-2xl font-bold">{scoreDescriptions.length}</p>
          <p className="text-foreground-400 text-xs mt-0.5">per-category rubric detail</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Category Weights */}
        <div className="bg-white rounded-lg border border-background-200 p-5">
          <h2 className="text-primary-600 text-base font-semibold mb-4">Category Weights</h2>
          <div className="space-y-4">
            {slaWeights.map((w, idx) => {
              const colors = ["bg-secondary-500", "bg-primary-500", "bg-accent-500", "bg-background-500", "bg-foreground-400"];
              return (
                <div key={w.key}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-foreground-700 text-sm font-medium">{w.category}</span>
                  </div>
                  <WeightBar weight={w.weight} color={colors[idx]} />
                </div>
              );
            })}
          </div>
          <div className="mt-4 pt-3 border-t border-background-200">
            <p className="text-foreground-400 text-xs">
              Quality and timeliness carry the most weight (55% combined) as they directly impact service delivery outcomes.
            </p>
          </div>
        </div>

        {/* Score Bands */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-background-200 p-5">
          <h2 className="text-primary-600 text-base font-semibold mb-4">Score Bands & Outcomes</h2>
          <div className="space-y-3">
            {scoreBands.map((band) => (
              <div
                key={band.range}
                className="flex items-start gap-4 p-3 rounded-md border border-background-200 hover:border-background-300 transition-colors"
              >
                <div className={`w-10 h-10 rounded-full ${band.bgClass} flex items-center justify-center shrink-0`}>
                  <span className="text-white text-xs font-bold">{band.range}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`text-sm font-semibold ${band.textClass}`}>{band.label}</span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span className="text-foreground-500 text-xs font-medium">{band.recommendation}</span>
                  </div>
                  <p className="text-foreground-500 text-xs leading-relaxed">{band.action}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Full Scoring Rubric Matrix */}
      <div className="bg-white rounded-lg border border-background-200 overflow-hidden mb-6">
        <div className="px-5 py-4 border-b border-background-200">
          <h2 className="text-primary-600 text-base font-semibold">Scoring Rubric</h2>
          <p className="text-foreground-400 text-xs mt-0.5">
            Detailed descriptors for each score level across all five categories
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-background-200 bg-background-100">
                <th className="text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap w-[100px]">
                  Score
                </th>
                {categoryKeys.map((key) => (
                  <th
                    key={key}
                    className="text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap"
                  >
                    {shortLabels[key]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {scoreDescriptions.map((desc) => {
                const levelColors: Record<number, string> = {
                  5: "bg-secondary-50 border-secondary-200",
                  4: "bg-primary-50 border-primary-200",
                  3: "bg-accent-50 border-accent-200",
                  2: "bg-background-200 border-background-300",
                  1: "bg-foreground-100 border-foreground-200",
                };
                const levelTextColors: Record<number, string> = {
                  5: "text-secondary-700",
                  4: "text-primary-700",
                  3: "text-accent-700",
                  2: "text-foreground-600",
                  1: "text-foreground-600",
                };
                return (
                  <tr key={desc.score} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${levelColors[desc.score]} ${levelTextColors[desc.score]} border`}>
                          {desc.score}
                        </span>
                        <span className={`text-xs font-semibold ${levelTextColors[desc.score]}`}>{desc.label}</span>
                      </div>
                    </td>
                    {categoryKeys.map((key) => (
                      <td key={key} className="px-4 py-3 align-top">
                        <p className="text-foreground-600 text-xs leading-relaxed">{desc.descriptions[key]}</p>
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Demo Calculator */}
      <div className="bg-white rounded-lg border border-background-200 p-5">
        <h2 className="text-primary-600 text-base font-semibold mb-1">Example Calculation</h2>
        <p className="text-foreground-400 text-xs mb-5">
          See how the weighted formula converts individual category scores into a final recommendation
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Input scores */}
          <div>
            <div className="space-y-3">
              {slaWeights.map((w, idx) => {
                const key = w.key as keyof typeof demoReviewScores;
                const score = demoReviewScores[key];
                const scoreColor = score >= 4 ? "text-secondary-600" : score >= 3 ? "text-accent-600" : "text-foreground-500";
                const scoreBg = score >= 4 ? "bg-secondary-50" : score >= 3 ? "bg-accent-50" : "bg-foreground-100";
                return (
                  <div key={w.key} className="flex items-center justify-between p-2.5 rounded-md bg-background-50 border border-background-200">
                    <div className="flex items-center gap-2.5">
                      <span className="text-foreground-400 text-xs font-medium w-6 text-right">{idx + 1}</span>
                      <span className="text-foreground-700 text-sm">{w.category}</span>
                      <span className="text-foreground-400 text-xs">({w.weight}%)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold ${scoreBg} ${scoreColor}`}>
                        {score}
                      </span>
                      <span className="text-foreground-400 text-xs">× {w.weight}%</span>
                      <span className="text-foreground-600 text-xs font-medium w-10 text-right">
                        = {(score * w.weight / 100).toFixed(1)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 p-3 rounded-md bg-background-100 border border-background-200 flex items-center justify-between">
              <span className="text-foreground-700 text-sm font-semibold">Weighted Total</span>
              <span className="text-foreground-800 text-lg font-bold">{demoWeighted}</span>
            </div>
          </div>

          {/* Result */}
          <div className="flex flex-col items-center justify-center p-5 rounded-md border border-background-200 bg-background-50">
            <p className="text-foreground-400 text-xs font-medium uppercase tracking-wider mb-3">Result</p>
            <div className={`w-20 h-20 rounded-full ${demoBand.bgClass} flex items-center justify-center mb-3`}>
              <span className="text-white text-2xl font-bold">{demoWeighted}</span>
            </div>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold mb-1 ${demoBand.bgClass} bg-opacity-20 ${demoBand.textClass}`}>
              {demoBand.label}
            </span>
            <p className="text-foreground-500 text-xs text-center mt-2 max-w-[280px]">{demoBand.action}</p>
            <div className="mt-4 w-full">
              <div className="flex items-center gap-2">
                <span className="text-foreground-400 text-xs font-medium whitespace-nowrap">Recommendation:</span>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  demoBand.recommendation === "Auto-Renew" ? "bg-secondary-50 text-secondary-700" :
                  demoBand.recommendation === "Conditional Renewal" ? "bg-accent-50 text-accent-700" :
                  "bg-foreground-100 text-foreground-600"
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    demoBand.recommendation === "Auto-Renew" ? "bg-secondary-500" :
                    demoBand.recommendation === "Conditional Renewal" ? "bg-accent-500" :
                    "bg-foreground-500"
                  }`}></span>
                  {demoBand.recommendation}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}